import { PaintBucket, Home, Droplets, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const items = [
  {
    id: "interior",
    testId: SERVICES.interior,
    title: "Interior Painting",
    icon: PaintBucket,
    desc: "Walls, trim, ceilings, cabinets. Clean lines, careful prep, and zero-stress workdays in your home.",
    bullets: ["Color consultation", "Drywall & trim repair", "Low-VOC options"],
    span: "lg:col-span-2 lg:row-span-2",
    tone: "dark",
  },
  {
    id: "exterior",
    testId: SERVICES.exterior,
    title: "Exterior Painting",
    icon: Home,
    desc: "Stucco, siding, fascia, doors. Florida-grade coatings that stand up to coastal humidity and sun.",
    bullets: ["Power wash prep", "Caulk & seal", "Warranty-backed coatings"],
    span: "lg:col-span-1 lg:row-span-1",
    tone: "light",
  },
  {
    id: "pressure",
    testId: SERVICES.pressure,
    title: "Pressure Washing",
    icon: Droplets,
    desc: "Driveways, sidewalks, fences, decks, and full house wash-downs. Instant curb-appeal upgrade.",
    bullets: ["Soft wash for siding", "Concrete & pavers", "Roof-safe technique"],
    span: "lg:col-span-1 lg:row-span-1",
    tone: "accent",
  },
];

export default function Services() {
  return (
    <section id="services" className="bg-[#FAFAFA] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#0EA5E9]">What We Do</div>
            <h2 className="mt-3 font-heading font-black uppercase tracking-tight text-[#334155] text-4xl sm:text-5xl leading-[0.95]">
              Three Services.<br />Done Right.
            </h2>
          </div>
          <p className="max-w-md text-base text-[#4B5563]">
            One local crew handles your whole exterior refresh — wash first, then prime, then paint.
            No subcontractors, no surprises.
          </p>
        </div>

        <div className="mb-14 -mx-1 flex flex-wrap gap-2">
          {["Houses", "Apartments", "Condos", "Businesses", "Storefronts", "Offices", "HOAs", "Rentals", "& More"].map((t) => (
            <span
              key={t}
              className="inline-flex items-center bg-white border-2 border-[#334155] px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#334155]"
            >
              {t}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-2 gap-6 lg:gap-8 auto-rows-fr">
          {items.map((it) => (
            <ServiceCard key={it.id} item={it} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ item }) {
  const Icon = item.icon;
  const isDark = item.tone === "dark";
  const isAccent = item.tone === "accent";

  const bg = isDark
    ? "bg-[#334155] text-white"
    : isAccent
      ? "bg-[#0EA5E9] text-white"
      : "bg-white text-[#334155] border-2 border-[#334155]";

  return (
    <article
      data-testid={item.testId}
      className={`group relative overflow-hidden ${item.span} ${bg} flex flex-col min-h-[300px]`}
    >
      <div className="flex-1 p-7 sm:p-9 flex flex-col">
        <div className="flex items-center justify-between">
          <div className={`h-14 w-14 grid place-items-center ${isDark ? "bg-[#0EA5E9]" : isAccent ? "bg-white text-[#0EA5E9]" : "bg-[#334155] text-white"}`}>
            <Icon size={26} />
          </div>
          <ArrowUpRight
            size={22}
            className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
          />
        </div>

        <h3 className={`mt-8 font-heading font-extrabold uppercase tracking-tight leading-none ${isDark ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>
          {item.title}
        </h3>
        <p className={`mt-4 text-sm sm:text-base ${isDark ? "text-[#CBD5E1]" : isAccent ? "text-white/90" : "text-[#4B5563]"}`}>
          {item.desc}
        </p>

        <ul className={`mt-6 space-y-2 text-sm font-medium ${isDark ? "text-[#94A3B8]" : isAccent ? "text-white/85" : "text-[#4B5563]"}`}>
          {item.bullets.map((b) => (
            <li key={b} className="flex items-center gap-2">
              <span className={`h-1 w-3 ${isAccent ? "bg-white" : "bg-[#0EA5E9]"}`} />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}
